import type { ComponentPropsWithAs } from '@/@types/common'
import type { User } from '@/@types/user'
import { css } from '@emotion/react'
import { Avatar as BaseAvatar } from '@ruguoapp/jike-fe-components'
import { Link, type LinkProps } from '@tanstack/react-router'
import { get } from 'lodash-es'
import type { ComponentProps, ElementType } from 'react'

export interface AvatarProps {
	data: User | undefined

	/**
	 * avatarKey
	 * @default 'thumbnailUrl'
	 */
	avatarKey?: string
}

export default function Avatar<As extends ElementType = typeof Link>({
	as: As,
	data,
	avatarKey = 'thumbnailUrl',
	...props
}: ComponentPropsWithAs<AvatarProps, As>) {
	const Component = As ?? Link
	let defaultProps = {} as LinkProps

	if (Component === Link) {
		defaultProps = {
			onClick: (e: MouseEvent) => {
				e.stopPropagation()
			},
			to: '/u/$username',
			params: {
				username: data?.username,
			},
		} as ComponentProps<As>
	}

	return (
		<Component
			css={css`
				position: relative;
				height: 42px;
				width: 42px;
				display: block;
			`}
			{...defaultProps}
			{...props}
		>
			{data && (
				<>
					<BaseAvatar
						width={42}
						height={42}
						src={get(data.avatarImage, avatarKey)}
						css={css`
							height: 100%;
							width: 100%;
						`}
					/>
					{data.isSponsor && data.decorations?.sponsor?.picUrl && (
						<img
							width={14}
							height={14}
							loading="lazy"
							referrerPolicy="no-referrer"
							src={data.decorations?.sponsor?.picUrl}
							className="sponsor-icon"
							alt="sponsor"
							css={css`
								position: absolute;
								z-index: 1;
								right: -2px;
								bottom: -2px;
								width: 14px;
								height: 14px;
							`}
						/>
					)}
				</>
			)}
		</Component>
	)
}
